import {
  NotificationOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { ReactComponent as CheckFolderIcon } from 'assets/icons/CheckFolder.svg';
import { ReactComponent as FileAddIcon } from 'assets/icons/FileAdd.svg';
import dayjs from 'dayjs';
import { ComponentType } from 'react';
import {
  normalizeNotificationType,
  Notification,
  NotificationAddress,
  NotificationType,
} from 'types/Notification';

const ONE_MINUTE_MS = 60 * 1000;
const ONE_HOUR_MINUTES = 60;

export const NOTIFICATIONS_REFETCH_INTERVAL_MS = 60 * ONE_MINUTE_MS;

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface NotificationIcon {
  Icon: ComponentType<{ className?: string }>;
  containerClassName: string;
  iconClassName: string;
}

export interface NotificationDisplayData {
  path: string;
  isUnread: boolean;
  title: string;
  description: string;
  location: string;
  dateLabel: string;
  icon: NotificationIcon;
}

const DEFAULT_NOTIFICATION_ICON: NotificationIcon = {
  Icon: NotificationOutlined,
  containerClassName: 'bg-bleu-hellio-1',
  iconClassName: 'text-bleu-hellio-6',
};

const RED_SYNC_ICON: NotificationIcon = {
  Icon: SyncOutlined,
  containerClassName: 'bg-rouge-copro-1',
  iconClassName: 'text-rouge-copro-10',
};

const ORANGE_SYNC_ICON: NotificationIcon = {
  Icon: SyncOutlined,
  containerClassName: 'bg-orange-particuliers-1',
  iconClassName: 'text-orange-particuliers-6',
};

const ORANGE_DOCUMENT_ICON: NotificationIcon = {
  Icon: FileAddIcon,
  containerClassName: 'bg-orange-particuliers-1',
  iconClassName: 'text-orange-particuliers-6',
};

const RED_DOCUMENT_ICON: NotificationIcon = {
  Icon: FileAddIcon,
  containerClassName: 'bg-rouge-copro-1',
  iconClassName: 'text-rouge-copro-10',
};

const GREEN_DOCUMENT_ICON: NotificationIcon = {
  Icon: FileAddIcon,
  containerClassName: 'bg-vert-transport-1',
  iconClassName: 'text-vert-transport-11',
};

const GREEN_CHECK_ICON: NotificationIcon = {
  Icon: CheckFolderIcon,
  containerClassName: 'bg-vert-transport-1',
  iconClassName: 'text-vert-transport-11',
};

const GREEN_FLOW_ICON: NotificationIcon = {
  Icon: SyncOutlined,
  containerClassName: 'bg-vert-transport-1',
  iconClassName: 'text-vert-transport-11',
};

const NOTIFICATION_ICONS_BY_TYPE: Record<NotificationType, NotificationIcon> = {
  [NotificationType.RetourChiffreRecu]: RED_SYNC_ICON,
  [NotificationType.DossierEnAttenteDeDocuments]: ORANGE_DOCUMENT_ICON,
  [NotificationType.ChantierEnAttenteDeControle]: ORANGE_SYNC_ICON,
  [NotificationType.DocumentsManquantsOuNonConformes]: RED_DOCUMENT_ICON,
  [NotificationType.DemandePrimeTraitee]: ORANGE_DOCUMENT_ICON,
  [NotificationType.AttestationHonneurDisponible]: GREEN_DOCUMENT_ICON,
  [NotificationType.DossierValide]: GREEN_CHECK_ICON,
  [NotificationType.PaiementEffectue]: GREEN_FLOW_ICON,
};

export function getNotificationPath(notification: Notification): string {
  const dossierId = notification.data?.dossier?.id ?? notification.dossierId;
  const operationId =
    notification.data?.operation?.id ?? notification.operationId;

  if (!dossierId) {
    return '/dossiers';
  }

  return `/dossiers/${dossierId}${operationId ? `/${operationId}` : ''}`;
}

export function getNotificationLocation(
  notification: Notification,
  fallback: string,
): string {
  const locationLabel = formatNotificationLocation(notification.address);
  return locationLabel || fallback;
}

function normalizeLocationField(value: string | null | undefined): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim().replace(/\s+/g, ' ');
}

function formatNotificationLocation(
  location: NotificationAddress | null | undefined,
): string {
  if (!location) {
    return '';
  }

  const street = normalizeLocationField(location.street);
  const postalCode = normalizeLocationField(location.postalCode);
  const city = normalizeLocationField(location.city);
  const postalAndCity = [postalCode, city].filter(Boolean).join(' ');

  return [street, postalAndCity]
    .filter(Boolean)
    .join(', ')
    .replace(/\s+/g, ' ')
    .replace(/\s*,\s*/g, ', ')
    .trim();
}

export function getNotificationDateLabel(
  date: Date | string | null,
  t: TranslateFn,
): string {
  if (!date) {
    return '';
  }

  const notificationDate = dayjs(date);

  if (!notificationDate.isValid()) {
    return '';
  }

  const now = dayjs();
  const diffInMinutes = now.diff(notificationDate, 'minute');

  if (notificationDate.isSame(now, 'day') && diffInMinutes >= 0) {
    if (diffInMinutes < ONE_HOUR_MINUTES) {
      return t('notification:date_minutes', {
        count: Math.max(diffInMinutes, 1),
      });
    }

    return t('notification:date_hours', {
      count: Math.max(now.diff(notificationDate, 'hour'), 1),
    });
  }

  if (notificationDate.isSame(now.subtract(1, 'day'), 'day')) {
    return t('notification:yesterday');
  }

  return notificationDate.format('DD/MM/YYYY');
}

function getNotificationText(
  type: NotificationType | null,
  t: TranslateFn,
): Pick<NotificationDisplayData, 'title' | 'description'> {
  if (!type) {
    return {
      title: t('notification:content:default:title'),
      description: t('notification:content:default:description'),
    };
  }

  return {
    title: t(`notification:content:${type}:title`),
    description: t(`notification:content:${type}:description`),
  };
}

function getNotificationIcon(type: NotificationType | null): NotificationIcon {
  if (!type) {
    return DEFAULT_NOTIFICATION_ICON;
  }

  return NOTIFICATION_ICONS_BY_TYPE[type] ?? DEFAULT_NOTIFICATION_ICON;
}

export function buildNotificationDisplayData(
  notification: Notification,
  t: TranslateFn,
): NotificationDisplayData {
  const type = normalizeNotificationType(notification.type);
  const text = getNotificationText(type, t);

  return {
    path: getNotificationPath(notification),
    isUnread: !notification.readDate,
    title: text.title,
    description: text.description,
    location: getNotificationLocation(
      notification,
      t('notification:location_unavailable'),
    ),
    dateLabel: getNotificationDateLabel(notification.date, t),
    icon: getNotificationIcon(type),
  };
}
